import { useState, useMemo, useCallback, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { parseISO, format } from 'date-fns';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ColDef, ValueFormatterParams, ICellRendererParams } from 'ag-grid-community';
import {
  ArrowLeft,
  Users,
  Shield,
  Save,
  Trash2,
  ShieldCheck,
  MoreHorizontal,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import {
  permissionAdminService,
  profileService,
  type PermissionRow,
  type PermissionUserRow,
  type PermissionGroupRow,
  type ServiceContext,
} from '@/services';
import { usePagination } from '@/hooks/usePagination';
import { AGDataGrid } from '@/components/ui/ag-data-grid';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import RecordMetadata from '@/components/RecordMetadata';
import { toast } from 'sonner';

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

function formatDateTime(dateStr: string | null): string {
  if (!dateStr) return '\u2014';
  try {
    return format(parseISO(dateStr), 'MMM d, yyyy h:mm a');
  } catch {
    return '\u2014';
  }
}

/* ------------------------------------------------------------------ */
/*  Users Tab                                                          */
/* ------------------------------------------------------------------ */

interface UsersTabProps {
  permissionId: string;
  companyId: string;
}

function UsersTab({ permissionId, companyId }: UsersTabProps) {
  const queryClient = useQueryClient();
  const [deleteTarget, setDeleteTarget] = useState<PermissionUserRow | null>(null);

  const {
    pageIndex,
    pageSize,
    searchValue,
    debouncedSearch,
    setSearchValue,
    setPageIndex,
    setPageSize,
    pageSizeOptions,
  } = usePagination({ defaultPageSize: 25, storageKey: 'perm-detail-users' });

  const { data: users, isLoading } = useQuery({
    queryKey: ['permission-users', permissionId, companyId],
    queryFn: async () => {
      const r = await permissionAdminService.listUsers(permissionId, companyId);
      if (r.error) throw r.error;
      return r.data;
    },
    staleTime: 30_000,
  });

  const filtered = useMemo(() => {
    if (!debouncedSearch || !users) return users ?? [];
    const q = debouncedSearch.toLowerCase();
    return users.filter(
      (u) => u.full_name?.toLowerCase().includes(q) ?? false
    );
  }, [users, debouncedSearch]);

  const paginated = useMemo(() => {
    const start = pageIndex * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, pageIndex, pageSize]);

  const handleRemove = useCallback(async () => {
    if (!deleteTarget) return;
    const result = await permissionAdminService.removeUser(
      permissionId,
      deleteTarget.profile_id,
      companyId
    );
    if (result.error) {
      toast.error(`Failed to remove: ${result.error.message}`);
    } else {
      toast.success(`Removed ${deleteTarget.full_name || 'user'}`);
      queryClient.invalidateQueries({ queryKey: ['permission-users', permissionId, companyId] });
    }
    setDeleteTarget(null);
  }, [deleteTarget, permissionId, companyId, queryClient]);

  const columnDefs = useMemo<ColDef<PermissionUserRow>[]>(
    () => [
      {
        headerName: 'Full Name',
        field: 'full_name',
        flex: 1,
        minWidth: 180,
        valueFormatter: (params: ValueFormatterParams<PermissionUserRow>) =>
          params.value ?? '\u2014',
      },
      {
        headerName: 'Assigned',
        field: 'assigned_at',
        width: 170,
        valueFormatter: (params: ValueFormatterParams<PermissionUserRow>) =>
          formatDateTime(params.value),
      },
      {
        colId: 'actions',
        headerName: '',
        width: 60,
        sortable: false,
        resizable: false,
        suppressColumnsToolPanel: true,
        cellRenderer: (params: ICellRendererParams<PermissionUserRow>) => {
          if (!params.data) return null;
          return (
            <div className="flex items-center justify-center h-full">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    className="text-destructive focus:text-destructive"
                    onClick={() => setDeleteTarget(params.data!)}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Remove
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          );
        },
      },
    ],
    []
  );

  return (
    <>
      <AGDataGrid<PermissionUserRow>
        rowData={paginated}
        columnDefs={columnDefs}
        totalCount={filtered.length}
        pageIndex={pageIndex}
        pageSize={pageSize}
        pageSizeOptions={pageSizeOptions}
        searchValue={searchValue}
        searchPlaceholder="Search users..."
        isLoading={isLoading}
        onSearchChange={setSearchValue}
        onPageChange={setPageIndex}
        onPageSizeChange={setPageSize}
        height="calc(100vh - 460px)"
      />

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove User Assignment</AlertDialogTitle>
            <AlertDialogDescription>
              Remove the direct permission assignment from "
              {deleteTarget?.full_name || 'this user'}"? The user may still
              have this permission through a group.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemove}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

/* ------------------------------------------------------------------ */
/*  Groups Tab                                                         */
/* ------------------------------------------------------------------ */

interface GroupsTabProps {
  permissionId: string;
  companyId: string;
}

function GroupsTab({ permissionId, companyId }: GroupsTabProps) {
  const queryClient = useQueryClient();
  const [deleteTarget, setDeleteTarget] = useState<PermissionGroupRow | null>(null);

  const {
    pageIndex,
    pageSize,
    searchValue,
    debouncedSearch,
    setSearchValue,
    setPageIndex,
    setPageSize,
    pageSizeOptions,
  } = usePagination({ defaultPageSize: 25, storageKey: 'perm-detail-groups' });

  const { data: groups, isLoading } = useQuery({
    queryKey: ['permission-groups', permissionId, companyId],
    queryFn: async () => {
      const r = await permissionAdminService.listGroups(permissionId, companyId);
      if (r.error) throw r.error;
      return r.data;
    },
    staleTime: 30_000,
  });

  const filtered = useMemo(() => {
    if (!debouncedSearch || !groups) return groups ?? [];
    const q = debouncedSearch.toLowerCase();
    return groups.filter(
      (g) =>
        g.group_name.toLowerCase().includes(q) ||
        (g.group_description?.toLowerCase().includes(q) ?? false)
    );
  }, [groups, debouncedSearch]);

  const paginated = useMemo(() => {
    const start = pageIndex * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, pageIndex, pageSize]);

  const handleRemove = useCallback(async () => {
    if (!deleteTarget) return;
    const result = await permissionAdminService.removeGroup(
      permissionId,
      deleteTarget.group_id,
      companyId
    );
    if (result.error) {
      toast.error(`Failed to remove: ${result.error.message}`);
    } else {
      toast.success(`Removed "${deleteTarget.group_name}"`);
      queryClient.invalidateQueries({ queryKey: ['permission-groups', permissionId, companyId] });
    }
    setDeleteTarget(null);
  }, [deleteTarget, permissionId, companyId, queryClient]);

  const columnDefs = useMemo<ColDef<PermissionGroupRow>[]>(
    () => [
      {
        headerName: 'Group Name',
        field: 'group_name',
        flex: 1,
        minWidth: 180,
      },
      {
        headerName: 'Description',
        field: 'group_description',
        flex: 1,
        minWidth: 200,
        valueFormatter: (params: ValueFormatterParams<PermissionGroupRow>) =>
          params.value ?? '\u2014',
      },
      {
        headerName: 'Active',
        field: 'is_active',
        width: 100,
        cellRenderer: (params: ICellRendererParams<PermissionGroupRow>) => {
          if (!params.data) return null;
          return (
            <span className={params.data.is_active ? 'text-green-600' : 'text-muted-foreground'}>
              {params.data.is_active ? 'Yes' : 'No'}
            </span>
          );
        },
      },
      {
        headerName: 'Assigned',
        field: 'assigned_at',
        width: 170,
        valueFormatter: (params: ValueFormatterParams<PermissionGroupRow>) =>
          formatDateTime(params.value),
      },
      {
        colId: 'actions',
        headerName: '',
        width: 60,
        sortable: false,
        resizable: false,
        suppressColumnsToolPanel: true,
        cellRenderer: (params: ICellRendererParams<PermissionGroupRow>) => {
          if (!params.data) return null;
          return (
            <div className="flex items-center justify-center h-full">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    className="text-destructive focus:text-destructive"
                    onClick={() => setDeleteTarget(params.data!)}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Remove
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          );
        },
      },
    ],
    []
  );

  return (
    <>
      <AGDataGrid<PermissionGroupRow>
        rowData={paginated}
        columnDefs={columnDefs}
        totalCount={filtered.length}
        pageIndex={pageIndex}
        pageSize={pageSize}
        pageSizeOptions={pageSizeOptions}
        searchValue={searchValue}
        searchPlaceholder="Search groups..."
        isLoading={isLoading}
        onSearchChange={setSearchValue}
        onPageChange={setPageIndex}
        onPageSizeChange={setPageSize}
        height="calc(100vh - 460px)"
      />

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Group Assignment</AlertDialogTitle>
            <AlertDialogDescription>
              Remove permission assignment from group "{deleteTarget?.group_name}"?
              Users in this group will lose this permission unless assigned directly.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemove}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

/* ------------------------------------------------------------------ */
/*  Page Component                                                     */
/* ------------------------------------------------------------------ */

export default function SecurityPermissionDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const queryClient = useQueryClient();

  // Edit state
  const [editName, setEditName] = useState('');
  const [editCode, setEditCode] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editCategory, setEditCategory] = useState('');
  const [editIsActive, setEditIsActive] = useState(true);
  const [hasEdits, setHasEdits] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Get companyId from the authenticated user's profile
  const { data: companyId } = useQuery({
    queryKey: ['my-company-id', authUser?.id],
    queryFn: async () => {
      const { data: profile } = await profileService.get(authUser!.id);
      return profile?.company_id ?? null;
    },
    enabled: !!authUser,
    staleTime: 10 * 60 * 1000,
  });

  // Fetch permission
  const { data: permission, isLoading } = useQuery({
    queryKey: ['permission-detail', id],
    queryFn: async () => {
      const r = await permissionAdminService.get(id!);
      if (r.error) throw r.error;
      if (!r.data) throw new Error('Permission not found');
      return r.data;
    },
    enabled: !!id,
  });

  // Sync edit state from fetched permission
  useEffect(() => {
    if (permission && !hasEdits) {
      setEditName(permission.name);
      setEditCode(permission.code);
      setEditDescription(permission.description ?? '');
      setEditCategory(permission.category ?? '');
      setEditIsActive(permission.is_active);
    }
  }, [permission, hasEdits]);

  // Track edits
  const handleFieldChange = useCallback(
    (setter: (v: string) => void) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setter(e.target.value);
      setHasEdits(true);
    },
    []
  );

  const handleActiveChange = useCallback((checked: boolean) => {
    setEditIsActive(checked);
    setHasEdits(true);
  }, []);

  const serviceContext = useMemo<ServiceContext>(
    () => ({ companyId: companyId!, userId: authUser?.id }),
    [companyId, authUser?.id]
  );

  const handleSave = useCallback(async () => {
    if (!permission || !companyId) return;

    const updates: Record<string, unknown> = {};
    if (editName !== permission.name) updates.name = editName;
    if (editCode !== permission.code) updates.code = editCode;
    if (editDescription !== (permission.description ?? ''))
      updates.description = editDescription || null;
    if (editCategory !== (permission.category ?? ''))
      updates.category = editCategory || null;
    if (editIsActive !== permission.is_active) updates.is_active = editIsActive;

    if (Object.keys(updates).length === 0) {
      toast.info('No changes to save');
      setHasEdits(false);
      return;
    }

    setIsSaving(true);
    try {
      const result = await permissionAdminService.update(
        permission.id,
        updates as Partial<{ name: string; code: string; description: string | null; category: string | null; is_active: boolean }>,
        permission as unknown as Record<string, unknown>,
        serviceContext
      );
      if (result.error) {
        toast.error(`Failed to save: ${result.error.message}`);
      } else {
        toast.success('Permission updated');
        setHasEdits(false);
        queryClient.invalidateQueries({ queryKey: ['permission-detail', id] });
      }
    } finally {
      setIsSaving(false);
    }
  }, [permission, companyId, editName, editCode, editDescription, editCategory, editIsActive, serviceContext, queryClient, id]);

  if (!id) {
    return <p className="p-4 text-muted-foreground">No permission ID provided.</p>;
  }

  if (isLoading) {
    return (
      <div className="animate-slide-up space-y-6 p-2">
        <Skeleton className="h-8 w-48" />
        <div className="space-y-2">
          <Skeleton className="h-10 w-96" />
          <Skeleton className="h-6 w-64" />
          <Skeleton className="h-20 w-full max-w-lg" />
        </div>
      </div>
    );
  }

  if (!permission) {
    return (
      <div className="p-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/security/permissions')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Permissions
        </Button>
        <p className="text-muted-foreground mt-4">Permission not found.</p>
      </div>
    );
  }

  return (
    <div className="animate-slide-up space-y-6">
      {/* Back button */}
      <Button variant="ghost" size="sm" onClick={() => navigate('/security/permissions')}>
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to Permissions
      </Button>

      {/* Header edit form */}
      <div className="space-y-4 max-w-2xl">
        {/* Name row with system badge */}
        <div className="flex items-center gap-3">
          <Input
            value={editName}
            onChange={handleFieldChange(setEditName)}
            className="text-2xl font-bold border-transparent hover:border-input focus:border-input h-auto py-1 px-2 -ml-2"
            placeholder="Permission name"
          />
          {permission.is_system && (
            <Badge variant="secondary" className="gap-1 shrink-0">
              <ShieldCheck className="h-3 w-3" />
              System
            </Badge>
          )}
        </div>

        {/* Code + Category */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="perm-code">Code</Label>
            <Input
              id="perm-code"
              value={editCode}
              onChange={handleFieldChange(setEditCode)}
              className="font-mono text-sm"
              placeholder="PERMISSION_CODE"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="perm-category">Category</Label>
            <Input
              id="perm-category"
              value={editCategory}
              onChange={handleFieldChange(setEditCategory)}
              placeholder="Category"
            />
          </div>
        </div>

        {/* Description */}
        <div className="space-y-1.5">
          <Label htmlFor="perm-desc">Description</Label>
          <Textarea
            id="perm-desc"
            value={editDescription}
            onChange={handleFieldChange(setEditDescription)}
            placeholder="Permission description..."
            rows={3}
          />
        </div>

        {/* Active toggle + Save */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Switch
              id="perm-active"
              checked={editIsActive}
              onCheckedChange={handleActiveChange}
            />
            <Label htmlFor="perm-active">Active</Label>
          </div>

          {hasEdits && (
            <Button onClick={handleSave} disabled={isSaving} size="sm">
              <Save className="h-4 w-4 mr-2" />
              {isSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          )}
        </div>
      </div>

      {/* Record metadata */}
      <RecordMetadata record={permission} />

      {/* Tabs */}
      {companyId && (
        <Tabs defaultValue="users" className="w-full">
          <TabsList className="w-full justify-start border-b border-border rounded-none bg-transparent h-auto p-0 mb-4">
            <TabsTrigger
              value="users"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-3 gap-1.5"
            >
              <Users className="h-4 w-4" />
              Users
            </TabsTrigger>
            <TabsTrigger
              value="groups"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-3 gap-1.5"
            >
              <Shield className="h-4 w-4" />
              Groups
            </TabsTrigger>
          </TabsList>

          <TabsContent value="users">
            <UsersTab permissionId={id} companyId={companyId} />
          </TabsContent>

          <TabsContent value="groups">
            <GroupsTab permissionId={id} companyId={companyId} />
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
